import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useGetMeQuery } from 'store/services/user/meApi';
import { useGetHaveListDefaultsQuery } from 'store/services/settings/settingsApi';
import { useBulkManageAlertsMutation } from 'store/services/collectionList/haveList/haveListApi';
import { useSmsAlertsIntroGate } from 'hooks/useSmsAlertsIntroGate';
import { SmsAlertsIntroModal } from 'components/SmsAlertsIntroModal/SmsAlertsIntroModal';
import { showSuccess, showError } from 'utils/toastNotifications';
import { AssetUrls } from 'config/assets';
import './BulkManageAlertsModal.css';

const BELL_ICON =
  'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/notifications_active_24dp.svg';
const LEARN_MORE_URL = 'https://trademagic.gg/articles/sms-trade-alerts';

const DEFAULT_THRESHOLD = '100';
const MIN_THRESHOLD = 1;
const MAX_THRESHOLD = 10000;
const MOBILE_BREAKPOINT_PX = 768;

type Mode = 'enable' | 'disable';

// Local viewport helper — mirrors the `useIsMobile` pattern in TradeCard.tsx:89.
// Inline because the helper isn't currently exported anywhere shared, and adding
// a second caller doesn't yet justify hoisting it to a hooks module.
function useIsMobileViewport(breakpoint = MOBILE_BREAKPOINT_PX): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === 'undefined' ? false : window.innerWidth < breakpoint,
  );
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);
  return isMobile;
}

interface BulkManageAlertsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BulkManageAlertsModal: React.FC<BulkManageAlertsModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [mode, setMode] = useState<Mode>('enable');
  const [threshold, setThreshold] = useState<string>(DEFAULT_THRESHOLD);
  const [autoEnableFuture, setAutoEnableFuture] = useState<boolean>(false);

  const { data: user } = useGetMeQuery(undefined, { skip: !isOpen });
  const { data: defaults } = useGetHaveListDefaultsQuery(undefined, { skip: !isOpen });
  const [bulkManageAlerts, { isLoading }] = useBulkManageAlertsMutation();
  const introGate = useSmsAlertsIntroGate();
  const isMobile = useIsMobileViewport();

  const phoneVerified = !!user?.phoneVerified;
  const totalCards = defaults?.totalCards ?? 0;
  const alertEnabledCount = defaults?.alertEnabledCount ?? 0;
  const alertsSentThisMonth = defaults?.alertsSentThisMonth ?? 0;
  const alertsMonthlyLimit = defaults?.alertsMonthlyLimit ?? 0;

  useEffect(() => {
    if (!isOpen) {
      setMode('enable');
      setThreshold(DEFAULT_THRESHOLD);
      setAutoEnableFuture(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose, isLoading]);

  if (!isOpen) return null;

  const parsedThreshold = threshold ? parseInt(threshold, 10) : NaN;
  const thresholdValid =
    !isNaN(parsedThreshold) &&
    parsedThreshold >= MIN_THRESHOLD &&
    parsedThreshold <= MAX_THRESHOLD;

  const canSubmit = phoneVerified && thresholdValid && !isLoading;

  const handleThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setThreshold(value);
    }
  };

  const runBulk = async (submitMode: Mode) => {
    try {
      const result = await bulkManageAlerts({
        minPointValue: parsedThreshold,
        mode: submitMode,
        // When checked, the backend additionally flips
        // `defaultHaveListAlert` to match the chosen mode in the same
        // transaction. Omitted → existing preference is untouched.
        ...(autoEnableFuture ? { autoEnableFuture: true } : {}),
      }).unwrap();

      const { affectedCount } = result;
      if (submitMode === 'enable') {
        if (affectedCount === 0) {
          showSuccess(
            `No cards in your Have List are worth ${parsedThreshold}+ points and not already alerted. Try a lower threshold or add cards to your Have List.`,
          );
        } else {
          showSuccess(
            `SMS alerts enabled on ${affectedCount} cards worth ${parsedThreshold}+ points.`,
          );
        }
      } else {
        if (affectedCount === 0) {
          showSuccess(
            `No cards worth ${parsedThreshold}+ points currently have alerts on.`,
          );
        } else {
          showSuccess(
            `SMS alerts disabled on ${affectedCount} cards worth ${parsedThreshold}+ points.`,
          );
        }
      }
      onClose();
    } catch {
      showError('Failed to update alerts. Please try again.');
    }
  };

  const handleSave = () => {
    if (!canSubmit) return;
    if (mode === 'enable') {
      introGate.guardEnable(() => {
        void runBulk('enable');
      });
    } else {
      void runBulk('disable');
    }
  };

  const handleOverlayClick = () => {
    if (!isLoading) onClose();
  };

  const modeCopy =
    mode === 'enable'
      ? `Set alerts on all cards worth at least ${parsedThreshold || '—'} points.`
      : `Turn off alerts on all cards worth at least ${parsedThreshold || '—'} points.`;

  const helperCopy =
    mode === 'enable'
      ? "Cards below this threshold won't be affected — your existing alert preferences stay the same."
      : "Cards below this threshold won't be affected — alerts on those cards stay as they are.";

  const modalContent = (
    <div
      className="bulk-alerts-modal-overlay"
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        className="bulk-alerts-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-alerts-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bulk-alerts-modal__bell" aria-hidden="true">
          <img
            src={BELL_ICON}
            alt=""
            className="bulk-alerts-modal__bell-icon"
          />
        </div>

        <h2 id="bulk-alerts-modal-title" className="bulk-alerts-modal__title">
          Manage SMS Alerts
        </h2>

        <div className="bulk-alerts-modal__divider" />

        <div className="bulk-alerts-modal__status">
          {phoneVerified ? (
            <>
              <p className="bulk-alerts-modal__status-line">
                {alertEnabledCount} of {totalCards} cards have alerts enabled
              </p>
              <p className="bulk-alerts-modal__status-line">
                You've used {alertsSentThisMonth} of {alertsMonthlyLimit} alerts this month.{' '}
                <a
                  href={LEARN_MORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bulk-alerts-modal__learn-more"
                >
                  Learn More
                </a>
              </p>
            </>
          ) : (
            <p className="bulk-alerts-modal__status-line bulk-alerts-modal__status-line--warn">
              Verify your phone in Settings to use SMS alerts
            </p>
          )}
        </div>

        <div className="bulk-alerts-modal__divider" />

        <div
          className="bulk-alerts-modal__mode-toggle"
          role="radiogroup"
          aria-label="Choose action"
        >
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'enable'}
            className={`bulk-alerts-modal__mode-btn ${mode === 'enable' ? 'active' : ''}`}
            onClick={() => setMode('enable')}
            disabled={!phoneVerified || isLoading}
          >
            Enable
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={mode === 'disable'}
            className={`bulk-alerts-modal__mode-btn ${mode === 'disable' ? 'active' : ''}`}
            onClick={() => setMode('disable')}
            disabled={!phoneVerified || isLoading}
          >
            Disable
          </button>
        </div>

        <p className="bulk-alerts-modal__mode-copy">{modeCopy}</p>

        <div className="bulk-alerts-modal__threshold-row">
          <label
            htmlFor="bulk-alerts-modal-threshold"
            className="bulk-alerts-modal__threshold-pill"
          >
            <div className="bulk-alerts-modal__threshold-pill-content">
              <img
                src={AssetUrls.icons.profile.coin}
                alt=""
                aria-hidden="true"
                className="bulk-alerts-modal__threshold-coin"
              />
              <input
                id="bulk-alerts-modal-threshold"
                type="text"
                inputMode="numeric"
                className="bulk-alerts-modal__threshold-input"
                value={threshold}
                onChange={handleThresholdChange}
                disabled={!phoneVerified || isLoading}
                aria-label="Minimum point value"
                maxLength={5}
                // Desktop: focus so the gold caret is visible and the field reads
                // as typeable. Mobile: skip — autoFocus there forces the on-screen
                // keyboard up before the user is ready.
                autoFocus={!isMobile && phoneVerified}
              />
            </div>
          </label>
          <span className="bulk-alerts-modal__threshold-suffix">points</span>
        </div>

        <p className="bulk-alerts-modal__helper">{helperCopy}</p>

        <label className="bulk-alerts-modal__future-toggle">
          <input
            type="checkbox"
            className="bulk-alerts-modal__future-checkbox"
            checked={autoEnableFuture}
            onChange={(e) => setAutoEnableFuture(e.target.checked)}
            disabled={!phoneVerified || isLoading}
          />
          <span className="bulk-alerts-modal__future-label">
            {mode === 'enable'
              ? 'Also enable alerts on cards I add in the future'
              : 'Also stop auto-enabling alerts on cards I add in the future'}
          </span>
        </label>

        <div className="bulk-alerts-modal__actions">
          <button
            type="button"
            className="bulk-alerts-modal__save"
            onClick={handleSave}
            disabled={!canSubmit}
          >
            {isLoading ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="bulk-alerts-modal__cancel"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </div>

      <SmsAlertsIntroModal
        isOpen={introGate.isModalOpen}
        onConfirm={introGate.handleConfirm}
        onDismiss={introGate.handleDismiss}
      />
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default BulkManageAlertsModal;
